import React, { useState, useEffect } from 'react';
import { Save, AlertCircle } from 'lucide-react';
import Modal from './Modal';

const FixedExpenseForm = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  expense = null, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    amount: '',
    category: '',
    dueDay: '',
    alertDaysBefore: '3'
  });
  
  const [errors, setErrors] = useState({});
  
  const categories = [
    'Energia',
    'Água', 
    'Internet',
    'Telefone',
    'Aluguel',
    'Segurança',
    'Limpeza',
    'Manutenção',
    'Outros'
  ];
  
  // Preencher formulário quando editando
  useEffect(() => {
    if (expense) {
      setFormData({
        name: expense.name || '',
        description: expense.description || '',
        amount: expense.amount?.toString() || '',
        category: expense.category || '',
        dueDay: expense.dueDay?.toString() || '',
        alertDaysBefore: expense.alertDaysBefore?.toString() || '3'
      });
    } else {
      setFormData({
        name: '',
        description: '',
        amount: '',
        category: '',
        dueDay: '',
        alertDaysBefore: '3'
      });
    }
    setErrors({});
  }, [expense, isOpen]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Limpar erro do campo quando usuário digita
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Valor deve ser maior que zero';
    }
    
    if (!formData.category) {
      newErrors.category = 'Categoria é obrigatória';
    }
    
    if (!formData.dueDay || parseInt(formData.dueDay) < 1 || parseInt(formData.dueDay) > 31) {
      newErrors.dueDay = 'Dia de vencimento deve estar entre 1 e 31';
    }
    
    if (formData.alertDaysBefore && (parseInt(formData.alertDaysBefore) < 0 || parseInt(formData.alertDaysBefore) > 30)) {
      newErrors.alertDaysBefore = 'Dias de alerta deve estar entre 0 e 30';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const submitData = {
      ...formData,
      amount: parseFloat(formData.amount),
      dueDay: parseInt(formData.dueDay),
      alertDaysBefore: parseInt(formData.alertDaysBefore)
    };
    
    onSubmit(submitData);
  };
  
  const formatCurrency = (value) => {
    // Remove tudo que não é número ou vírgula/ponto
    const numericValue = value.replace(/[^\d.,]/g, '');
    
    // Converte vírgula para ponto
    const normalizedValue = numericValue.replace(',', '.');
    
    return normalizedValue;
  };
  
  const handleAmountChange = (e) => {
    const formatted = formatCurrency(e.target.value);
    setFormData(prev => ({ ...prev, amount: formatted }));
    
    if (errors.amount) {
      setErrors(prev => ({ ...prev, amount: '' }));
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={expense ? 'Editar Despesa Fixa' : 'Nova Despesa Fixa'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome */}
          <div className="form-group">
            <label className="form-label">
              Nome da Despesa *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`form-input ${
                errors.name ? 'border-red-500' : ''
              }`}
              placeholder="Ex: Conta de Energia"
              disabled={loading}
            />
            {errors.name && (
              <p className="form-error flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.name}
              </p>
            )}
          </div>
          
          {/* Descrição */}
          <div className="form-group">
            <label className="form-label">
              Descrição
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="form-textarea"
              placeholder="Descrição adicional (opcional)"
              disabled={loading}
            />
          </div>
          
          {/* Valor e Categoria */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Valor (R$) *
              </label>
              <input
                type="text"
                name="amount"
                value={formData.amount}
                onChange={handleAmountChange}
                className={`form-input ${
                  errors.amount ? 'border-red-500' : ''
                }`}
                placeholder="0,00"
                disabled={loading}
              />
              {errors.amount && (
                <p className="form-error flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.amount}
                </p>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label">
                Categoria *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={`form-select ${
                  errors.category ? 'border-red-500' : ''
                }`}
                disabled={loading}
              >
                <option value="">Selecione...</option>
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              {errors.category && (
                <p className="form-error flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.category}
                </p>
              )}
            </div>
          </div>
          
          {/* Dia de Vencimento e Alerta */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Dia de Vencimento *
              </label>
              <input
                type="number"
                name="dueDay"
                value={formData.dueDay}
                onChange={handleChange}
                min="1"
                max="31"
                className={`form-input ${
                  errors.dueDay ? 'border-red-500' : ''
                }`}
                placeholder="Ex: 15"
                disabled={loading}
              />
              {errors.dueDay && (
                <p className="form-error flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.dueDay}
                </p>
              )}
            </div>
            
            <div className="form-group">
              <label className="form-label">
                Alertar (dias antes)
              </label>
              <input
                type="number"
                name="alertDaysBefore"
                value={formData.alertDaysBefore}
                onChange={handleChange}
                min="0"
                max="30"
                className={`form-input ${
                  errors.alertDaysBefore ? 'border-red-500' : ''
                }`}
                disabled={loading}
              />
              {errors.alertDaysBefore && (
                <p className="form-error flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.alertDaysBefore}
                </p>
              )}
            </div>
          </div>
          
          {/* Botões */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outline"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {expense ? 'Atualizar' : 'Salvar'}
            </button>
          </div>
        </form>
    </Modal>
  );
};

export default FixedExpenseForm;